import { queryOptions } from '@tanstack/react-query';
import { api, isWorkingCopyPreparing } from '@/api';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import type {
  BidMethodologyCategory,
  BidMethodologySection,
  GetBidMethodologyVersionsResponse,
  MethodologyPendingOperation,
} from '@/features/bid-methodology/types';
import type { ChatHydrateMessage } from '@/features/chat/types';
import type { VersionStatus } from '@/features/workspaces/types';
import { AnalysisStatus, REFETCH_SUMMARIZATION_INTERVAL } from '@/features/workspaces/constants';

export const bidMethodologyChatHydrateQuery = (bidId: string, sessionId: string) =>
  queryOptions({
    queryKey: ['bidMethodologyChatHydrate', bidId, sessionId],
    queryFn: async () =>
      (
        await api.get<{
          messages: ChatHydrateMessage[];
          pendingOperations: MethodologyPendingOperation[];
        }>('v1/analysis/bid_methodology/chat_hydrate', {
          params: { chat_session_id: sessionId, type: DOCUMENT_TYPE.bid, resourceId: bidId },
        })
      ).data,
  });

export const BID_METHODOLOGY_SUMMARY_QUERY_KEY = 'bidMethodologySummary';

export const bidMethodologySummaryQuery = (
  bidId: string,
  evaluationVersionId?: string,
  bidMethodologyId?: string,
) =>
  queryOptions({
    queryKey: [BID_METHODOLOGY_SUMMARY_QUERY_KEY, bidId, evaluationVersionId, bidMethodologyId],
    queryFn: async () =>
      (
        await api.get<{
          bidTitle: string;
          totalScore: number;
          good: number;
          needsImprovement: number;
          status: AnalysisStatus;
          progress: number;
          overallSummary: string | null;
          versionStatus?: VersionStatus;
          isMethodologyStale?: boolean;
        }>('v1/analysis/bid_methodology', {
          params: {
            type: DOCUMENT_TYPE.bid,
            resourceId: bidId,
            evaluationVersionId,
            bidMethodologyId,
          },
        })
      ).data,
  });

export const BID_METHODOLOGY_CATEGORIES_QUERY_KEY = 'bidMethodologyCategories';

export const bidMethodologyCategoriesQuery = (
  bidId: string,
  evaluationVersionId?: string,
  bidMethodologyId?: string,
) =>
  queryOptions({
    queryKey: [BID_METHODOLOGY_CATEGORIES_QUERY_KEY, bidId, evaluationVersionId, bidMethodologyId],
    queryFn: async () =>
      (
        await api.get<{
          bidTitle: string;
          totalScore: number;
          good: number;
          needsImprovement: number;
          status: number;
          progress: number;
          categories: BidMethodologyCategory[];
          versionStatus?: VersionStatus;
        }>('v1/analysis/bid_methodology/categories', {
          params: {
            type: DOCUMENT_TYPE.bid,
            resourceId: bidId,
            evaluationVersionId,
            bidMethodologyId,
          },
        })
      ).data,
  });

export const BID_METHODOLOGY_SECTION_QUERY_KEY = 'bidMethodologySection';

export const bidMethodologySectionQuery = (
  bidId: string,
  sectionId: string,
  evaluationVersionId?: string,
  bidMethodologyId?: string,
) =>
  queryOptions({
    queryKey: [
      BID_METHODOLOGY_SECTION_QUERY_KEY,
      bidId,
      sectionId,
      evaluationVersionId,
      bidMethodologyId,
    ],
    queryFn: async () =>
      (
        await api.get<BidMethodologySection>(`v1/analysis/bid_methodology/sections/${sectionId}`, {
          params: {
            type: DOCUMENT_TYPE.bid,
            resourceId: bidId,
            evaluationVersionId,
            bidMethodologyId,
          },
        })
      ).data,
  });

export const bidMethodologyDocumentQuery = (
  bidId: string,
  docId: string,
  evaluationVersionId?: string,
  bidMethodologyId?: string,
) =>
  queryOptions({
    queryKey: ['bidMethodologyDocument', bidId, docId, evaluationVersionId, bidMethodologyId],
    retry: (_count, error) => isWorkingCopyPreparing(error),
    retryDelay: 2000,
    queryFn: async () =>
      (
        await api.get<{
          id: string | null;
          docId: string;
          organizationId: string;
          tenderId: string;
          bidId: string;
          evaluationVersionId: string;
          blocks: any[];
          source: 'layout_seeded' | 'user_edited';
          updatedAt: string;
          updatedBy: string;
          pageLimit?: { pages: number; section: string } | null;
        }>(`v1/analysis/bid_methodology/document/${docId}`, {
          params: {
            type: DOCUMENT_TYPE.bid,
            resourceId: bidId,
            evaluationVersionId,
            bidMethodologyId,
            waitSeconds: 2,
          },
        })
      ).data,
  });

export const bidMethodologyVersionsQuery = (bidId: string) =>
  queryOptions({
    queryKey: ['bidMethodologyVersions', bidId],
    queryFn: async ({ client }) => {
      const data = (
        await api.get<GetBidMethodologyVersionsResponse>('v1/analysis/bid_methodology/versions', {
          params: { type: DOCUMENT_TYPE.bid, resourceId: bidId },
        })
      ).data;
      const runningVersion = data.versions.find(
        (version) => version.status === AnalysisStatus.IN_PROGRESS,
      );
      if (!runningVersion) return data;

      try {
        const analysis = await client.query({
          ...bidMethodologyCategoriesQuery(bidId, undefined, runningVersion.bidMethodologyId),
          staleTime: 0,
          retry: false,
        });
        return {
          ...data,
          versions: data.versions.map((version) =>
            version.bidMethodologyId === runningVersion.bidMethodologyId
              ? { ...version, progress: analysis.progress }
              : version,
          ),
        };
      } catch {
        return data;
      }
    },
    refetchInterval: (query) =>
      query.state.data?.versions.some((version) => version.status === AnalysisStatus.IN_PROGRESS)
        ? REFETCH_SUMMARIZATION_INTERVAL * 1000
        : false,
  });

export const savedSuggestionsQuery = (bidId: string) =>
  queryOptions({
    queryKey: ['savedSuggestions', bidId],
    queryFn: async () =>
      (
        await api.get<{
          savedSuggestions: {
            id: string;
            suggestionKind: 'in_document' | 'blocked' | 'strategic' | 'recurring_gap';
            eventType:
              | 'generated'
              | 'applied'
              | 'declined'
              | 'dismissed'
              | 'acknowledged'
              | 'cannot_deliver';
            title: string | null;
            missingSource: string | null;
            gapDescription: string | null;
            targetDocId: string | null;
            actorId: string | null;
            createdAt: string;
          }[];
        }>('v1/analysis/bid_methodology/saved-suggestions', {
          params: { type: DOCUMENT_TYPE.bid, resourceId: bidId },
        })
      ).data,
  });
